import i18next from "i18next";

export type Language = "en" | "ru";

export interface I18nResources {
  readonly en: object;
  readonly ru: object;
}

function syncDocumentLocale(): void {
  // `seo.title` is the same descriptive title the build writes into the static
  // HTML (see @bn-games/vite-seo), so switching language never degrades the tab
  // or rendered-page title to the bare app name. Every app's dictionary has it;
  // the per-app i18next.d.ts augmentation makes it a valid typed key.
  document.title = i18next.t("seo.title");
  document.documentElement.lang = i18next.language;
}

/**
 * The language the page itself was served in — `ru` for the `/…/ru/` URLs
 * @bn-games/vite-seo emits, else `en`. Apps use it as the default UI language
 * when the player has no stored preference, so a Russian URL opens in Russian.
 */
export function pageLanguage(): Language {
  return typeof document !== "undefined" && document.documentElement.lang === "ru" ? "ru" : "en";
}

export async function initI18n(lang: Language, resources: I18nResources): Promise<void> {
  await i18next.init({
    lng: lang,
    fallbackLng: "en",
    resources: {
      en: { translation: resources.en },
      ru: { translation: resources.ru },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  syncDocumentLocale();
}

export async function setLocale(lang: Language): Promise<void> {
  await i18next.changeLanguage(lang);
  syncDocumentLocale();
}

export const t = i18next.t.bind(i18next);
